import React, { useEffect, useState } from "react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Upload, Image as ImageIcon, Eye } from "lucide-react";
import { Link } from "react-router-dom";

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "EKOBIOTEK",
  tags: [],
  language: "en",
  published: false,
};

const AdminBlog = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({ ...EMPTY, ...p, tags: p.tags || [] });

  const slugify = (text) =>
    text
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const save = async () => {
    if (!editing) return;
    const body = { ...editing };
    if (!body.slug) body.slug = slugify(body.title);
    try {
      if (editing.id) {
        await api.patch(`/admin/blog/${editing.id}`, body);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", body);
        toast.success("Post created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((p) => ({ ...p, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!editing.tags.includes(t)) {
      setEditing({ ...editing, tags: [...editing.tags, t] });
    }
    setTagInput("");
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline text-[#DE9E36]">Editorial</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-[#1A201C] tracking-tight">
            Blog posts
          </h1>
          <p className="mt-2 text-sm text-[#4E5750]">
            {items.length} posts · {items.filter((x) => x.published).length} published
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-post-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors"
        >
          <Plus size={16} /> New post
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#4E5750]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-14 border border-dashed border-[#E5E0D8] text-center text-sm text-[#4E5750]">
          No posts yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`post-${p.slug}`}
              className="border border-[#E5E0D8] bg-white overflow-hidden flex flex-col"
            >
              <div className="h-44 bg-[#F3EFE7] flex items-center justify-center overflow-hidden">
                {p.cover_image ? (
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={24} className="text-[#4E5750]/40" />
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#4E5750] font-mono">
                    {p.language}
                  </span>
                  <span
                    className={`text-[10px] tracking-[0.2em] uppercase ${
                      p.published ? "text-green-700" : "text-gray-500"
                    }`}
                  >
                    {p.published ? "Published" : "Draft"}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-[#1A201C] leading-snug">{p.title}</h3>
                <div className="text-xs text-[#4E5750] mt-1 font-mono truncate">{p.slug}</div>
                {p.excerpt && (
                  <p className="text-sm text-[#4E5750] mt-3 line-clamp-3">{p.excerpt}</p>
                )}
                <div className="mt-auto pt-4 flex gap-2 flex-wrap">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-post-${p.slug}`}
                    className="inline-flex items-center gap-1 px-3 py-2 text-xs tracking-[0.2em] uppercase border border-[#E5E0D8] hover:bg-[#F3EFE7]"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  {p.published && (
                    <Link
                      to={`/blog/${p.slug}`}
                      target="_blank"
                      className="inline-flex items-center gap-1 px-3 py-2 text-xs tracking-[0.2em] uppercase border border-[#E5E0D8] hover:bg-[#F3EFE7]"
                    >
                      <Eye size={12} /> View
                    </Link>
                  )}
                  <button
                    onClick={() => remove(p.id)}
                    className="inline-flex items-center gap-1 px-3 py-2 text-xs tracking-[0.2em] uppercase text-red-700 border border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setEditing(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FDFBF7] w-full max-w-4xl my-10"
            data-testid="post-editor"
          >
            <div className="sticky top-0 bg-[#1A201C] text-white px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="overline text-[#DE9E36]">
                  {editing.id ? "Edit post" : "New post"}
                </div>
                <div className="text-lg font-bold">{editing.title || "Untitled"}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 md:p-8 space-y-5">
              {/* Cover image */}
              <Field label="Cover image">
                <div className="flex items-start gap-4">
                  <div className="w-48 h-32 bg-[#F3EFE7] border border-[#E5E0D8] overflow-hidden flex items-center justify-center">
                    {editing.cover_image ? (
                      <img src={editing.cover_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={24} className="text-[#4E5750]/40" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="inline-flex items-center gap-2 px-4 py-3 bg-[#1A201C] text-[#DE9E36] text-xs tracking-[0.2em] uppercase cursor-pointer hover:bg-[#DE9E36] hover:text-[#1A201C] transition-colors">
                      <Upload size={14} />
                      {uploading ? "Uploading…" : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="post-image-upload"
                        onChange={(e) => uploadImage(e.target.files?.[0])}
                      />
                    </label>
                    <input
                      type="text"
                      placeholder="or paste image URL"
                      value={editing.cover_image || ""}
                      onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                      className="w-full h-10 px-3 border border-[#E5E0D8] bg-white text-sm"
                    />
                  </div>
                </div>
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Title">
                  <input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    data-testid="post-title"
                    className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                  />
                </Field>
                <Field label="Slug (auto-generated if blank)">
                  <input
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="my-first-post"
                    className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm font-mono"
                  />
                </Field>
                <Field label="Author">
                  <input
                    value={editing.author || ""}
                    onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                    className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                  />
                </Field>
                <Field label="Language">
                  <select
                    value={editing.language}
                    onChange={(e) => setEditing({ ...editing, language: e.target.value })}
                    className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                  >
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="ua">Українська</option>
                  </select>
                </Field>
              </div>

              <Field label="Excerpt (1-2 sentences shown in cards)">
                <textarea
                  value={editing.excerpt || ""}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  rows={3}
                  className="w-full p-3 border border-[#E5E0D8] bg-white text-sm"
                />
              </Field>

              <Field label="Content (plain text or HTML)">
                <textarea
                  value={editing.content || ""}
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  rows={12}
                  data-testid="post-content"
                  placeholder="Write your article here. Plain paragraphs separated by blank lines, or HTML."
                  className="w-full p-3 border border-[#E5E0D8] bg-white text-sm font-mono"
                />
              </Field>

              <Field label="Tags">
                <div className="flex flex-wrap gap-2 mb-2">
                  {editing.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-2 px-3 py-1 text-xs bg-[#F3EFE7] text-[#1A201C]"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({ ...editing, tags: editing.tags.filter((x) => x !== t) })
                        }
                        className="text-[#4E5750] hover:text-red-700"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Add a tag, then press Enter"
                    className="flex-1 h-10 px-3 border border-[#E5E0D8] bg-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 border border-[#E5E0D8] text-xs tracking-[0.2em] uppercase hover:bg-[#F3EFE7]"
                  >
                    Add
                  </button>
                </div>
              </Field>

              <label className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={!!editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                  className="w-4 h-4"
                />
                Published
              </label>
            </div>

            <div className="sticky bottom-0 bg-[#FDFBF7] border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setEditing(null)}
                className="px-5 py-3 text-xs tracking-[0.22em] uppercase border border-[#E5E0D8]"
              >
                Cancel
              </button>
              <button
                onClick={save}
                data-testid="save-post"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors"
              >
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="overline text-[#4E5750] mb-1.5">{label}</div>
    {children}
  </div>
);

export default AdminBlog;
